import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Loader2,
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Crown,
  Ticket,
  Plus,
  Trash2,
  Copy,
  Percent,
  Calendar,
  Eye,
  RefreshCw,
  CreditCard,
  Settings,
  UserCheck,
  UserX,
  Shield,
  Wrench,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const PlatformDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [showTrialDialog, setShowTrialDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [creatingCode, setCreatingCode] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [newTrialDate, setNewTrialDate] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [bulkRole, setBulkRole] = useState({ role: 'SalesRep', rep_tier: 'Broker 1' });
  const [fixingLeads, setFixingLeads] = useState(false);
  const [newCode, setNewCode] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    duration: 'once',
    duration_months: 3,
    max_redemptions: '',
    expires_at: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, tenantsRes, codesRes, usersRes] = await Promise.all([
        api.get('/platform/stats'),
        api.get('/platform/tenants'),
        api.get('/platform/discount-codes'),
        api.get('/platform/users'),
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data);
      setDiscountCodes(codesRes.data);
      setAllUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load platform data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiscount = async () => {
    if (!newCode.code || !newCode.discount_value) {
      toast.error('Please fill in code and discount value');
      return;
    }
    
    setCreatingCode(true);
    try {
      const payload = {
        code: newCode.code.toUpperCase().replace(/\s/g, ''),
        discount_type: newCode.discount_type,
        discount_value: parseFloat(newCode.discount_value),
        duration: newCode.duration,
        duration_months: newCode.duration === 'repeating' ? parseInt(newCode.duration_months) : null,
        max_redemptions: newCode.max_redemptions ? parseInt(newCode.max_redemptions) : null,
        expires_at: newCode.expires_at || null,
      };
      
      await api.post('/platform/discount-codes', payload);
      toast.success(`Discount code ${payload.code} created!`);
      setShowDiscountDialog(false);
      setNewCode({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        duration: 'once',
        duration_months: 3,
        max_redemptions: '',
        expires_at: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount code');
    } finally {
      setCreatingCode(false);
    }
  };

  const handleDeleteDiscount = async (codeId, codeName) => {
    if (!window.confirm(`Delete discount code "${codeName}"?`)) return;
    try {
      await api.delete(`/platform/discount-codes/${codeId}`);
      toast.success('Discount code deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete discount code');
    }
  };

  const handleToggleDiscount = async (codeId, currentlyActive) => {
    try {
      await api.put(`/platform/discount-codes/${codeId}`, { is_active: !currentlyActive });
      toast.success(`Discount code ${currentlyActive ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update discount code');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied "${code}" to clipboard`);
  };

  const handleSuspend = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to suspend ${orgName}?`)) return;
    try {
      await api.post(`/platform/tenants/${orgId}/suspend`);
      toast.success('Tenant suspended');
      loadData();
    } catch (error) {
      toast.error('Failed to suspend tenant');
    }
  };

  const handleReactivate = async (orgId) => {
    try {
      await api.post(`/platform/tenants/${orgId}/reactivate`);
      toast.success('Tenant reactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to reactivate tenant');
    }
  };

  // View tenant details
  const handleViewTenant = async (tenant) => {
    try {
      const res = await api.get(`/platform/tenants/${tenant.id}`);
      setSelectedTenant(res.data);
      setShowTenantDialog(true);
    } catch (error) {
      toast.error('Failed to load tenant details');
    }
  };

  // Extend trial
  const handleExtendTrial = async () => {
    if (!selectedTenant || !newTrialDate) return;
    setProcessingAction(true);
    try {
      await api.put(`/platform/tenants/${selectedTenant.id}/trial`, {
        trial_ends_at: new Date(newTrialDate).toISOString()
      });
      toast.success('Trial extended successfully');
      setShowTrialDialog(false);
      setNewTrialDate('');
      loadData();
    } catch (error) {
      toast.error('Failed to extend trial');
    } finally {
      setProcessingAction(false);
    }
  };

  // Reset trial
  const handleResetTrial = async (orgId) => {
    if (!window.confirm('Reset to a fresh 14-day trial?')) return;
    try {
      await api.post(`/platform/tenants/${orgId}/reset-trial`);
      toast.success('Trial reset to 14 days');
      loadData();
    } catch (error) {
      toast.error('Failed to reset trial');
    }
  };

  // Convert to paid
  const handleConvertToPaid = async (orgId) => {
    if (!window.confirm('Convert this tenant to a paid subscription?')) return;
    try {
      await api.post(`/platform/tenants/${orgId}/convert-to-paid`);
      toast.success('Converted to paid subscription');
      loadData();
    } catch (error) {
      toast.error('Failed to convert to paid');
    }
  };

  // Fix lead organization assignments
  const handleFixLeadOrganizations = async () => {
    if (!window.confirm('This will fix all leads with incorrect organization assignments. Continue?')) return;
    setFixingLeads(true);
    try {
      const response = await api.post('/platform/fix-lead-organizations');
      const { fixed_count, total_leads, errors } = response.data;
      if (fixed_count > 0) {
        toast.success(`Fixed ${fixed_count} out of ${total_leads} leads`);
      } else {
        toast.info(`All ${total_leads} leads already have correct organization assignments`);
      }
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} leads had errors`);
      }
    } catch (error) {
      toast.error('Failed to fix lead organizations');
    } finally {
      setFixingLeads(false);
    }
  };

  // Change subscription status
  const handleChangeStatus = async () => {
    if (!selectedTenant || !newStatus) return;
    setProcessingAction(true);
    try {
      await api.put(`/platform/tenants/${selectedTenant.id}/subscription`, {
        subscription_status: newStatus
      });
      toast.success(`Status changed to ${newStatus}`);
      setShowStatusDialog(false);
      setNewStatus('');
      loadData();
    } catch (error) {
      toast.error('Failed to change status');
    } finally {
      setProcessingAction(false);
    }
  };

  // Bulk user status
  const handleBulkUserStatus = async (isActive) => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    try {
      const res = await api.post('/users/bulk/status', {
        user_ids: selectedUsers,
        is_active: isActive
      });
      toast.success(`${res.data.success_count} users ${isActive ? 'enabled' : 'disabled'}`);
      if (res.data.failed_count > 0) {
        toast.warning(`${res.data.failed_count} users failed`);
      }
      setSelectedUsers([]);
      loadData();
    } catch (error) {
      toast.error('Failed to update users');
    }
  };

  // Bulk role change
  const handleBulkRoleChange = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    try {
      const res = await api.post('/users/bulk/role', {
        user_ids: selectedUsers,
        role: bulkRole.role,
        rep_tier: bulkRole.rep_tier || null
      });
      toast.success(`${res.data.success_count} users updated`);
      if (res.data.failed_count > 0) {
        toast.warning(`${res.data.failed_count} users failed`);
      }
      setSelectedUsers([]);
      loadData();
    } catch (error) {
      toast.error('Failed to update roles');
    }
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select all users
  const selectAllUsers = () => {
    if (selectedUsers.length === allUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(allUsers.map(u => u.id));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      trial: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      past_due: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      cancelled: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    };
    const icons = {
      active: <CheckCircle className="w-3 h-3 mr-1" />,
      trial: <Clock className="w-3 h-3 mr-1" />,
      past_due: <AlertTriangle className="w-3 h-3 mr-1" />,
      suspended: <XCircle className="w-3 h-3 mr-1" />,
      cancelled: <XCircle className="w-3 h-3 mr-1" />,
    };
    return (
      <Badge className={styles[status] || styles.trial}>
        {icons[status]}
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="platform-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-6 h-6 text-amber-500" />
            <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
              Platform Owner Dashboard
            </h1>
          </div>
          <p className="text-slate-600 dark:text-slate-400">
            Manage all tenant organizations and monitor platform health
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">Total Tenants</p>
                <p className="text-3xl font-bold text-emerald-800 dark:text-emerald-200 mt-1">
                  {stats?.total_tenants || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-200 dark:bg-emerald-800 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-700 dark:text-emerald-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 border-blue-200 dark:border-blue-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-300">Active Subscribers</p>
                <p className="text-3xl font-bold text-blue-800 dark:text-blue-200 mt-1">
                  {stats?.active_tenants || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-blue-700 dark:text-blue-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10 border-amber-200 dark:border-amber-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700 dark:text-amber-300">On Trial</p>
                <p className="text-3xl font-bold text-amber-800 dark:text-amber-200 mt-1">
                  {stats?.trial_tenants || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-700 dark:text-amber-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-800/10 border-purple-200 dark:border-purple-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300">Monthly Revenue</p>
                <p className="text-3xl font-bold text-purple-800 dark:text-purple-200 mt-1">
                  ${(stats?.mrr || 0).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-200 dark:bg-purple-800 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-700 dark:text-purple-300" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Tools */}
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-800/50 dark:to-slate-900/50 border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-slate-700 dark:text-slate-300">
            <Wrench className="w-5 h-5" />
            Maintenance Tools
          </CardTitle>
          <CardDescription>
            Administrative tools for data maintenance and troubleshooting
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={handleFixLeadOrganizations} 
              disabled={fixingLeads}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="fix-lead-assignments-btn"
            >
              {fixingLeads ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <AlertCircle className="w-4 h-4 text-amber-500" />
              )}
              Fix Lead Assignments
            </Button>
            <p className="text-sm text-slate-500 dark:text-slate-400 self-center">
              Fixes leads with incorrect organization assignments so reps can see their assigned leads
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" />
            All Tenant Organizations
          </CardTitle>
          <CardDescription>
            Manage and monitor all organizations using your platform
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {tenants.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Organization</TableHead>
                    <TableHead>Subdomain</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Leads</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                      <TableCell className="font-medium">{tenant.name}</TableCell>
                      <TableCell>
                        <code className="text-sm bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                          {tenant.subdomain}.puresolarsystems.com
                        </code>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 dark:text-slate-400">
                        {tenant.owner_email}
                      </TableCell>
                      <TableCell>{getStatusBadge(tenant.subscription_status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-slate-400" />
                          {tenant.users_count}
                        </div>
                      </TableCell>
                      <TableCell>{tenant.leads_count}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(tenant.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`tenant-actions-${tenant.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => handleViewTenant(tenant)}>
                              <Eye className="w-4 h-4 mr-2 text-blue-600" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedTenant(tenant);
                              setShowTrialDialog(true);
                            }}>
                              <Calendar className="w-4 h-4 mr-2 text-purple-600" />
                              Extend Trial
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResetTrial(tenant.id)}>
                              <RefreshCw className="w-4 h-4 mr-2 text-cyan-600" />
                              Reset Trial (14 days)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleConvertToPaid(tenant.id)}>
                              <CreditCard className="w-4 h-4 mr-2 text-emerald-600" />
                              Convert to Paid
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedTenant(tenant);
                              setNewStatus(tenant.subscription_status);
                              setShowStatusDialog(true);
                            }}>
                              <Settings className="w-4 h-4 mr-2 text-slate-600" />
                              Change Status
                            </DropdownMenuItem>
                            <div className="h-px bg-slate-200 dark:bg-slate-700 my-1" />
                            {tenant.subscription_status === 'suspended' ? (
                              <DropdownMenuItem onClick={() => handleReactivate(tenant.id)}>
                                <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                                Reactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => handleSuspend(tenant.id, tenant.name)}
                                className="text-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Suspend
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No tenants yet
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Organizations will appear here when they sign up for your platform.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue Info */}
      <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium opacity-90">Platform Revenue</h3>
              <p className="text-3xl font-bold mt-1">
                ${((stats?.active_tenants || 0) * 349.99).toLocaleString()}/mo
              </p>
              <p className="text-sm opacity-75 mt-1">
                {stats?.active_tenants || 0} active × $349.99/month
              </p>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-8 h-8 opacity-75" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Discount Codes Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Ticket className="w-5 h-5 text-purple-600" />
              Discount Codes
            </CardTitle>
            <CardDescription>
              Create and manage promotional codes for new subscribers
            </CardDescription>
          </div>
          <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-discount-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Code
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Create Discount Code</DialogTitle>
                <DialogDescription>
                  Create a promotional code for new tenant signups
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Promo Code</Label>
                  <Input
                    id="code"
                    placeholder="e.g., LAUNCH50"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    className="font-mono"
                    data-testid="discount-code-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Discount Type</Label>
                    <Select
                      value={newCode.discount_type}
                      onValueChange={(v) => setNewCode({ ...newCode, discount_type: v })}
                    >
                      <SelectTrigger data-testid="discount-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Off</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Value</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder={newCode.discount_type === 'percentage' ? '50' : '100'}
                        value={newCode.discount_value}
                        onChange={(e) => setNewCode({ ...newCode, discount_value: e.target.value })}
                        className="pr-10"
                        data-testid="discount-value-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        {newCode.discount_type === 'percentage' ? '%' : '$'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select
                    value={newCode.duration}
                    onValueChange={(v) => setNewCode({ ...newCode, duration: v })}
                  >
                    <SelectTrigger data-testid="discount-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once">First Month Only</SelectItem>
                      <SelectItem value="repeating">Multiple Months</SelectItem>
                      <SelectItem value="forever">Forever</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {newCode.duration === 'repeating' && (
                  <div className="space-y-2">
                    <Label>Number of Months</Label>
                    <Input
                      type="number"
                      min="1"
                      max="24"
                      value={newCode.duration_months}
                      onChange={(e) => setNewCode({ ...newCode, duration_months: e.target.value })}
                      data-testid="discount-months-input"
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max Redemptions (optional)</Label>
                    <Input
                      type="number"
                      placeholder="Unlimited"
                      value={newCode.max_redemptions}
                      onChange={(e) => setNewCode({ ...newCode, max_redemptions: e.target.value })}
                      data-testid="discount-max-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expires On (optional)</Label>
                    <Input
                      type="date"
                      value={newCode.expires_at}
                      onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                      data-testid="discount-expires-input"
                    />
                  </div>
                </div>
                
                {/* Preview */}
                {newCode.code && newCode.discount_value && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">Preview:</p>
                    <p className="text-purple-800 dark:text-purple-200 font-mono text-lg">
                      {newCode.code.toUpperCase()} → {' '}
                      {newCode.discount_type === 'percentage' 
                        ? `${newCode.discount_value}% off` 
                        : `$${newCode.discount_value} off`}
                      {' '}
                      {newCode.duration === 'once' && 'first month'}
                      {newCode.duration === 'repeating' && `for ${newCode.duration_months} months`}
                      {newCode.duration === 'forever' && 'forever'}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDiscountDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateDiscount} disabled={creatingCode} data-testid="create-discount-submit">
                  {creatingCode && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create Code
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          {discountCodes.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Code</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Used</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {discountCodes.map((code) => (
                    <TableRow key={code.id} data-testid={`discount-row-${code.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="font-mono font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded">
                            {code.code}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(code.code)}
                            className="h-7 w-7 p-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {code.discount_type === 'percentage' ? (
                            <>
                              <Percent className="w-4 h-4 text-emerald-500" />
                              <span className="font-medium">{code.discount_value}% off</span>
                            </>
                          ) : (
                            <>
                              <DollarSign className="w-4 h-4 text-emerald-500" />
                              <span className="font-medium">${code.discount_value} off</span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {code.duration === 'once' && 'First month'}
                        {code.duration === 'repeating' && `${code.duration_months} months`}
                        {code.duration === 'forever' && 'Forever'}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono">
                          {code.times_redeemed}
                          {code.max_redemptions && ` / ${code.max_redemptions}`}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={code.is_active 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                        }>
                          {code.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => copyToClipboard(code.code)}>
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Code
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleDiscount(code.id, code.is_active)}>
                              {code.is_active ? (
                                <>
                                  <XCircle className="w-4 h-4 mr-2 text-amber-600" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteDiscount(code.id, code.code)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Ticket className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No discount codes yet
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Create promotional codes to attract new subscribers
              </p>
              <Button onClick={() => setShowDiscountDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Code
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Users Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                All Platform Users
              </CardTitle>
              <CardDescription>
                Manage users across all tenant organizations
              </CardDescription>
            </div>
            {selectedUsers.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{selectedUsers.length} selected</span>
                <Button size="sm" variant="outline" onClick={() => handleBulkUserStatus(true)}>
                  <UserCheck className="w-4 h-4 mr-1" />
                  Enable
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkUserStatus(false)}>
                  <UserX className="w-4 h-4 mr-1" />
                  Disable
                </Button>
                <Select value={bulkRole.role} onValueChange={(v) => setBulkRole({...bulkRole, role: v})}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="SalesRep">SalesRep</SelectItem>
                    <SelectItem value="BackOffice">BackOffice</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={handleBulkRoleChange}>
                  Apply Role
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {allUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === allUsers.length && allUsers.length > 0}
                        onChange={selectAllUsers}
                        className="rounded border-slate-300"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allUsers.slice(0, 50).map((u) => (
                    <TableRow key={u.id} className={selectedUsers.includes(u.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(u.id)}
                          onChange={() => toggleUserSelection(u.id)}
                          className="rounded border-slate-300"
                        />
                      </TableCell>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm text-slate-600 dark:text-slate-400">{u.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{u.organization_name}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={u.role === 'Admin' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'}>
                          {u.role}
                          {u.rep_tier && ` (${u.rep_tier})`}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.is_active !== false ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Disabled</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {allUsers.length > 50 && (
                <div className="text-center py-4 text-sm text-slate-500">
                  Showing 50 of {allUsers.length} users
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No users found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tenant Details Dialog */}
      <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              {selectedTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Detailed information about this tenant organization
            </DialogDescription>
          </DialogHeader>
          {selectedTenant && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Owner</Label>
                  <p className="font-medium">{selectedTenant.owner_name}</p>
                  <p className="text-sm text-slate-500">{selectedTenant.owner_email}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedTenant.subscription_status)}</div>
                </div>
                <div>
                  <Label className="text-slate-500">Trial Ends</Label>
                  <p className="font-medium">
                    {selectedTenant.trial_ends_at 
                      ? new Date(selectedTenant.trial_ends_at).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Created</Label>
                  <p className="font-medium">{new Date(selectedTenant.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Users</Label>
                  <p className="font-medium">{selectedTenant.users_count}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Leads</Label>
                  <p className="font-medium">{selectedTenant.leads_count}</p>
                </div>
                {selectedTenant.stripe_customer_id && (
                  <div className="col-span-2">
                    <Label className="text-slate-500">Stripe Customer ID</Label>
                    <code className="block text-sm bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded mt-1">
                      {selectedTenant.stripe_customer_id}
                    </code>
                  </div>
                )}
              </div>
              
              {selectedTenant.users && selectedTenant.users.length > 0 && (
                <div>
                  <Label className="text-slate-500 mb-2 block">Users in this Organization</Label>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedTenant.users.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell className="text-sm">{u.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {u.role}{u.rep_tier ? ` (${u.rep_tier})` : ''}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extend Trial Dialog */}
      <Dialog open={showTrialDialog} onOpenChange={setShowTrialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Trial</DialogTitle>
            <DialogDescription>
              Set a new trial end date for {selectedTenant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Current Trial Ends</Label>
              <p className="text-sm text-slate-500 mt-1">
                {selectedTenant?.trial_ends_at 
                  ? new Date(selectedTenant.trial_ends_at).toLocaleString()
                  : 'Not set'}
              </p>
            </div>
            <div>
              <Label htmlFor="newTrialDate">New Trial End Date</Label>
              <Input
                id="newTrialDate"
                type="datetime-local"
                value={newTrialDate}
                onChange={(e) => setNewTrialDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTrialDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleExtendTrial} disabled={processingAction || !newTrialDate}>
                {processingAction && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Extend Trial
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Subscription Status</DialogTitle>
            <DialogDescription>
              Update the subscription status for {selectedTenant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active (Paid)</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleChangeStatus} disabled={processingAction || !newStatus}>
                {processingAction && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformDashboard;
